/**
 * ADR 023 — Predicate-as-Truth lifecycle phase tokens, discriminated-union
 * return types, and the minimal input shapes the predicates read.
 *
 * This file is the SINGLE source of the phase vocabulary shared by BOTH forms
 * of the predicate (BL-639 / "BL-3" of the ADR023 build-out):
 *   - the in-memory IsomorphicServices `deriveAuctionPhase` / `deriveLotPhase`
 *     (derive-auction-phase.ts / derive-lot-phase.ts), and
 *   - the SQL phase-WHERE builder `auctionPhaseWhere` / `lotPhaseWhere`
 *     (phase-where.ts).
 * The Postgres parity test asserts the two forms classify every row identically
 * for every token below — that test is the anti-drift gate between them.
 *
 * Pure types only — NO `@auctogon/db` / `drizzle-orm` / React import (this file
 * is reachable from the client via the lifecycle barrel; the SQL builder, which
 * DOES import the schema, is reached only via its own server-only subpath).
 *
 * ## Canonical precedence ladders (first match wins — TS and SQL must agree)
 *
 * The phase of a row is the FIRST kind whose condition holds, top to bottom.
 * Each SQL `*PhaseWhere(token)` mirrors a ladder rung by ANDing the rung's own
 * condition with the negation of every higher rung.
 *
 * ### Auction (timed). Over non-deleted lots:
 *   maxClose   = MAX(lot.closeTime)
 *   1  deleted    ← deletedAt
 *   2  closed     ← closedAt
 *   3  draft              ← readiedAt IS NULL
 *   4  accepting-pre-bids ← openedAt IS NULL AND preBiddingAt IS NOT NULL
 *                           (opensAt = startDate)
 *   5  ready              ← openedAt IS NULL                   (opensAt = startDate)
 *   6  paused             ← pausedAt IS NOT NULL
 *   7  active             ← otherwise                          (closesAt = maxClose)
 *
 * (BL-1291: the `accepting-pre-bids` rung. Requiring `openedAt IS NULL` is what
 * makes pre-bid/bid exclusivity free — the moment the auction opens, the rung
 * stops reading the stamp, so `paused`/`active` keep their existing precedence
 * over a still-set `pre_bidding_at`.)
 *
 * (BL-1516: the `post-sale` rung — `allTerminal AND (maxClose IS NULL OR now >=
 * maxClose)`, sitting between `paused` and `active` — was retired. It named the
 * window between "every lot has finished" and "`closed_at` has been stamped",
 * which the per-minute close sweep already closes, so it was a bookkeeping gap
 * rather than a state of the business and no operator could act on it. The
 * PREDICATE survives, deliberately not as a status: `isAuctionAwaitingClose`
 * (in-memory) / `auctionAwaitingCloseWhere` (SQL) answer "has this sale
 * finished?" for the close sweep and the gates that genuinely need it.)
 *
 * (BL-717: the `closing` rung — `closingStartedAt IS NOT NULL` — was retired.
 * It mirrored the pre-BL-661 operator-close mutex, which BL-661 stopped
 * writing; `closing_started_at` has been permanently NULL since, so the rung
 * was unreachable dead code. Dropped alongside the column.)
 *
 * ### Lot (timed). windowOpen = (auction.openedAt IS NOT NULL AND biddingOpenedAt
 *     IS NOT NULL) OR (auction.preBiddingAt IS NOT NULL AND preBiddingAt IS NOT NULL):
 *   1  deleted              ← deletedAt
 *   2  withdrawn            ← withdrawnAt
 *   3  sold                 ← hammeredAt
 *   4  unsold               ← closeProcessedAt (no terminal stamp above)
 *                             reason = 'no-bids' if bidsPlacedCount = 0 else 'reserve-not-met'
 *   5  paused               ← pausedAt
 *   6  awaiting-resolution  ← windowOpen AND closeTime IS NOT NULL AND now >= closeTime
 *                             AND auction.closingType !== 'manual'
 *   6b last-call            ← windowOpen AND closeTime IS NOT NULL AND now >= closeTime
 *                             AND auction.closingType === 'manual' (BUG-909). Same
 *                             rung as awaiting-resolution — the two conditions are
 *                             mutually exclusive by closingType, so precedence order
 *                             between them doesn't matter. A manual-close lot past its
 *                             scheduled close stays biddable ("Last call") until a
 *                             clerk resolves it; an auto-close lot in the identical
 *                             stamp-state is `awaiting-resolution` (closed to bids).
 *   7  open-for-bidding     ← auction.openedAt IS NOT NULL AND biddingOpenedAt IS NOT NULL
 *                             AND (closeTime IS NULL OR now < closeTime)
 *   8  pre-bidding          ← auction.preBiddingAt IS NOT NULL AND preBiddingAt IS NOT
 *                             NULL AND (closeTime IS NULL OR now < closeTime)
 *                             (BL-1291 two-stamp: mirrors open-for-bidding's
 *                             auction-then-lot shape. Clearing the AUCTION stamp
 *                             darkens every lot at once without touching a lot row,
 *                             and re-stamping restores them — losslessly, because
 *                             the lot stamps were never cleared.)
 *   9  ready                ← readiedAt (BL-772: a biddingOpenedAt-but-not-open lot
 *                             also lands here — the old `published` rung was retired;
 *                             biddingOpenedAt is only ever stamped with readiedAt)
 *   10 draft                ← otherwise
 *
 * ### Lot — null-auction (unassociated) case (BL-941 / BL-939)
 * A lot minted with `auction_id IS NULL` (not yet added to any auction) has no
 * auction to derive `windowOpen` from — rungs 7–9 (`awaiting-resolution` /
 * `open-for-bidding` / `pre-bidding`) are structurally unreachable, and rung 10
 * (`ready`) can never read `readiedAt` (that stamp is only ever set while
 * readying a lot WITHIN an auction). Rungs 1–6 (terminal + paused) are
 * unaffected — they read only lot-own columns. BUG-1110: an unassociated lot is
 * therefore ALWAYS `draft` — `ready` is reachable only inside an auction:
 *   10 ready  ← readiedAt AND auction_id IS NOT NULL
 *   11 draft  ← otherwise (every unassociated lot lands here)
 */

// ---------------------------------------------------------------------------
// Return types — discriminated unions (mirror ADR 023 Decision 2)
// ---------------------------------------------------------------------------

/** Derived auction lifecycle phase. `opensAt`/`closesAt` are ISO timestamptz
 *  strings carried for the UI countdown. */
export type AuctionPhase =
  | { kind: 'draft' }
  /** BL-1291 — pre-bidding is open on the auction; bidding has not. Carries the
   *  same `opensAt` as `ready` (the scheduled start is still what it counts to). */
  | { kind: 'accepting-pre-bids'; opensAt: string }
  | { kind: 'ready'; opensAt: string }
  | { kind: 'active'; closesAt: string | null }
  | { kind: 'paused'; pausedAt: string }
  | { kind: 'closed'; closedAt: string }
  | { kind: 'deleted' };

/** Derived lot lifecycle phase. */
export type LotPhase =
  | { kind: 'draft' }
  | { kind: 'ready' }
  | { kind: 'pre-bidding'; closesAt: string | null }
  | { kind: 'open-for-bidding'; closesAt: string | null }
  | { kind: 'awaiting-resolution'; closedAt: string }
  // BUG-909: manual-close analog of awaiting-resolution — past scheduled
  // close, unresolved, but still biddable (a clerk closes it, not the timer).
  | { kind: 'last-call'; closedAt: string }
  | { kind: 'paused'; pausedAt: string }
  // BL-2067: the token carries no price. The lot's current price is realtime-
  // projected (rule #32) and nothing read it here; it only put a currency-less
  // amount on REST payloads that embed the token (the rings / lot-order DTO).
  | { kind: 'sold'; hammeredAt: string; winnerId: string | null }
  | { kind: 'unsold'; closedAt: string | null; reason: 'reserve-not-met' | 'no-bids' }
  | { kind: 'withdrawn'; withdrawnAt: string }
  | { kind: 'deleted' };

/** Every auction phase token, in precedence order. The SQL builder + parity
 *  test iterate this so no token is silently uncovered. */
export const AUCTION_PHASE_KINDS = [
  'deleted',
  'closed',
  'draft',
  'accepting-pre-bids',
  'ready',
  'paused',
  'active',
] as const;
export type AuctionPhaseKind = (typeof AUCTION_PHASE_KINDS)[number];

/** Every lot phase token, in precedence order. */
export const LOT_PHASE_KINDS = [
  'deleted',
  'withdrawn',
  'sold',
  'unsold',
  'paused',
  'awaiting-resolution',
  'last-call',
  'open-for-bidding',
  'pre-bidding',
  'ready',
  'draft',
] as const;
export type LotPhaseKind = (typeof LOT_PHASE_KINDS)[number];

/**
 * The operator status-label space (mirrors the drained `auction_lot_status`
 * enum), derived by `deriveLotStatusLabelFromStamps`. Lives here alongside
 * {@link LotPhaseKind} so response DTOs (ADR 027) and the app-side derive helper
 * share one declaration. The app helper re-exports this for its existing callers.
 */
export type LotStatusLabel =
  | 'draft'
  | 'ready'
  | 'active'
  | 'pre_bidding'
  | 'paused'
  | 'sold'
  | 'unsold'
  | 'withdrawn'
  | 'deleted';

// ---------------------------------------------------------------------------
// Input shapes — minimal fields, structural (per ADR 010 / sharing.md §3, both
// the raw Drizzle row and a snapshot projection satisfy these by structure).
// Timestamps are ISO timestamptz strings (Drizzle `mode: 'string'`) or null.
// ---------------------------------------------------------------------------

/** The auction columns the auction predicate reads. */
export interface AuctionPhaseInput {
  /** Scheduled start (auctions.start_date, NOT NULL) — `opensAt` for `ready`. */
  startDate: string;
  readiedAt: string | null;
  openedAt: string | null;
  closedAt: string | null;
  deletedAt: string | null;
  pausedAt: string | null;
  /** `auctions.pre_bidding_at` (BL-1291) — the auction's own pre-bidding stamp.
   *  Read only while `openedAt` is null; opening the auction ends pre-bidding
   *  with no separate "stop pre-bidding" write. */
  preBiddingAt: string | null;
}

/** The per-lot fields the AUCTION predicate reads to derive maxClose + allTerminal. */
export interface LotCloseInput {
  closeTime: string | null;
  hammeredAt: string | null;
  withdrawnAt: string | null;
  closeProcessedAt: string | null;
  deletedAt: string | null;
}

/** The auction columns the LOT predicate reads (window-open derivation). */
export interface LotPhaseAuctionInput {
  openedAt: string | null;
  /**
   * BL-1291 — `auctions.pre_bidding_at`. REQUIRED (not optional) for the same
   * reason `closingType` is: a caller that silently omits it would reproduce
   * exactly the asymmetry this field exists to remove — a lot answering "am I
   * pre-bidding?" from its own stamp alone, so one leftover lot pins an auction
   * in pre-bidding and closing pre-bidding means chasing individual lots.
   * Making it required means tsc enumerates every call site.
   */
  preBiddingAt: string | null;
  /**
   * BUG-909: required so the awaiting-resolution rung can split into
   * `awaiting-resolution` (auto-close, closed to bids) vs `last-call`
   * (manual-close, still biddable — a clerk resolves it). Required, not
   * optional, so a caller can't silently omit it and reproduce the bug this
   * field exists to fix (the grid card was the one caller with no access to
   * it). Callers that don't need the split (admin surfaces, which fold both
   * kinds back to "active" regardless) pass `'automatic'` — behavior-
   * preserving, since that's the value that always resolved
   * `awaiting-resolution` before this field existed.
   */
  closingType: 'manual' | 'automatic';
}

/** The lot columns the lot predicate reads.
 *
 * `winnerCustomerId` is OPTIONAL (BL-645): it's carried into the `sold` return
 * metadata but is NOT a phase predicate (the `hammeredAt` stamp drives the
 * kind). The realtime `LotSnapshot` projection deliberately omits it — it's
 * customer identity, not public-safe on the anon bidder channel — so a
 * snapshot-derived phase leaves it undefined (→ `null` in the return).
 * Server-side callers that hold the full `auction_lots` row still pass it.
 *
 * BL-687: `withdrawnByStaffUserId` was removed — the withdrawal actor is no
 * longer stored on the lot (dropped from `auction_lots`); it lives in the
 * audit log. The `withdrawn` token now carries only `withdrawnAt`.
 *
 * BUG-1110 removed `cataloguingComplete` (BL-941). It existed solely to feed the
 * null-auction ready/draft branch, which now returns `draft` unconditionally —
 * so the field had no reader left, and ADR 044 — Must Consolidate makes an
 * alternative that still exists an unfinished consolidation. BL-1643 finished
 * that consolidation by dropping `auction_lots.cataloguing_complete` itself.
 *
 * BL-2067 removed `acceptedBidUnitAmountDenormalized` the same way: it only
 * filled the `sold` token's price, which nothing read. A caller never has to
 * encode the lot's Money column (ADR 060 — Money Type) to get a phase. */
export interface LotPhaseInput {
  deletedAt: string | null;
  withdrawnAt: string | null;
  hammeredAt: string | null;
  closeProcessedAt: string | null;
  pausedAt: string | null;
  closeTime: string | null;
  biddingOpenedAt: string | null;
  preBiddingAt: string | null;
  readiedAt: string | null;
  bidsPlacedCountDenormalized: number;
  winnerCustomerId?: string | null;
}

/** The terminal (frozen-at-close) subset of {@link LotPhase} — the kinds a lot
 *  reaches once a disposition stamp lands, ordered by the precedence ladder. */
export type TerminalLotPhase = Extract<
  LotPhase,
  { kind: 'deleted' | 'withdrawn' | 'sold' | 'unsold' }
>;

/** The lot columns `deriveTerminalDisposition` reads — the terminal-stamp subset
 *  of {@link LotPhaseInput}. A full `LotPhaseInput` satisfies this structurally.
 *  Split out (BL-670) so the LIVE-safe terminal reader can run WITHOUT the
 *  auction inputs (`type`/`openedAt`) `deriveLotPhase` needs — terminal
 *  dispositions never depend on the auction's open state. */
export interface LotTerminalInput {
  deletedAt: string | null;
  withdrawnAt: string | null;
  hammeredAt: string | null;
  closeProcessedAt: string | null;
  bidsPlacedCountDenormalized: number;
  winnerCustomerId?: string | null;
}

